import { XY } from 'twrk/V2.js';
import { Shdr, shdr_type } from 'twrk/Shdr.js';
import { wbgl_type } from 'twrk/Wbgl.js';
import { scrn_type } from 'twrk/Scrn.js';
import { gl_Image } from 'twrk/gl_Image.js';

//a textured quad pinned to two moving world points: the quad is built once in image-frame
//pixels around pivot p1, and each render maps p1→w1, rotated so the p1→p2 line lies on w1→w2.
//image y runs down just like world y, so the mapping is a pure rotation + translation — parts
//cut from one shared frame recompose exactly when the world points sit in the drawn layout.

let _shader: shdr_type | null = null;

function get_shader(Canvas: wbgl_type): shdr_type {
    if (!_shader) {
        _shader = Shdr.make(Canvas, {
            id: "sprite_2d",
            vertex: `#version 300 es
            precision mediump float;
            in vec2 position;
            in vec2 uv;
            uniform vec2 u_screen;
            uniform float u_zoom;
            uniform vec2 u_pan;
            uniform vec2 u_pos;
            uniform vec2 u_rot; //cos/sin of the p1→p2 vs w1→w2 rotation
            out vec2 vUv;
            void main() {
                vUv = uv;
                vec2 p = u_pos + vec2(position.x * u_rot.x - position.y * u_rot.y, position.x * u_rot.y + position.y * u_rot.x);
                gl_Position = vec4((p - u_pan) * u_zoom * u_screen, 0.0, 1.0);
            }
            `,
            fragment: `#version 300 es
            precision mediump float;
            uniform sampler2D u_texture;
            in vec2 vUv;
            out vec4 fragColor;
            void main() {
                fragColor = texture(u_texture, vUv);
            }
            `
        });
    }
    return _shader;
}

export async function sprite_load(Canvas: wbgl_type, srcs: string[]) {
    await Promise.all(srcs.map(src => gl_Image.load(Canvas, src)));
}

export type sprite_input = {
    src: string; //must be sprite_load-ed before make
    frame: number; //source image size in px (square)
    size: number; //world height of the full frame
    p1: XY; //pivot in image px, lands on w1
    p2: XY; //second pin in image px, w1→w2 sets the rotation
    flip?: boolean; //mirror the artwork horizontally: pass p1/p2 already mirrored (x = frame - drawn x), this flips the texture u to match
};

export function sprite_make(Canvas: wbgl_type, Scrn: scrn_type, io: sprite_input) {

    const gl = Canvas.context;
    const shader = get_shader(Canvas);
    const prog = shader.compiled.program;

    const k = io.size / io.frame;
    const x0 = -io.p1.x * k, x1 = (io.frame - io.p1.x) * k;
    const y0 = -io.p1.y * k, y1 = (io.frame - io.p1.y) * k;
    const positions = new Float32Array([x0, y0, x1, y0, x0, y1, x1, y0, x1, y1, x0, y1]);
    const uvs = io.flip
        ? new Float32Array([1, 0, 0, 0, 1, 1, 0, 0, 0, 1, 1, 1])
        : new Float32Array([0, 0, 1, 0, 0, 1, 1, 0, 1, 1, 0, 1]);

    //image-space unit direction p1→p2, factored out of the per-frame rotation
    const il = Math.hypot(io.p2.x - io.p1.x, io.p2.y - io.p1.y);
    const ix = (io.p2.x - io.p1.x) / il;
    const iy = (io.p2.y - io.p1.y) / il;

    const u_screen = gl.getUniformLocation(prog, "u_screen");
    const u_zoom = gl.getUniformLocation(prog, "u_zoom");
    const u_pan = gl.getUniformLocation(prog, "u_pan");
    const u_pos = gl.getUniformLocation(prog, "u_pos");
    const u_rot = gl.getUniformLocation(prog, "u_rot");
    const u_texture = gl.getUniformLocation(prog, "u_texture");

    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    const buf_pos = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf_pos);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
    const loc_pos = gl.getAttribLocation(prog, "position");
    gl.enableVertexAttribArray(loc_pos);
    gl.vertexAttribPointer(loc_pos, 2, gl.FLOAT, false, 0, 0);
    const buf_uv = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf_uv);
    gl.bufferData(gl.ARRAY_BUFFER, uvs, gl.STATIC_DRAW);
    const loc_uv = gl.getAttribLocation(prog, "uv");
    gl.enableVertexAttribArray(loc_uv);
    gl.vertexAttribPointer(loc_uv, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);

    return {
        render: function (w1: XY, w2: XY, zoom: number, pan: XY) {

            const dx = w2.x - w1.x;
            const dy = w2.y - w1.y;
            const l = Math.hypot(dx, dy);
            if (l < 1e-6) return;
            const wx = dx / l, wy = dy / l;

            gl.useProgram(prog);
            gl.bindVertexArray(vao);
            gl.uniform2f(u_screen, Scrn.screen[0], Scrn.screen[1]);
            gl.uniform1f(u_zoom, zoom);
            gl.uniform2f(u_pan, pan.x, pan.y);
            gl.uniform2f(u_pos, w1.x, w1.y);
            gl.uniform2f(u_rot, wx * ix + wy * iy, wy * ix - wx * iy);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, gl_Image.list[io.src]);
            gl.uniform1i(u_texture, 0);
            gl.drawArrays(gl.TRIANGLES, 0, 6);
            gl.bindVertexArray(null);
        },
        kill: function () {
            gl.deleteBuffer(buf_pos);
            gl.deleteBuffer(buf_uv);
            gl.deleteVertexArray(vao);
        },
    };
}

export type sprite_t = ReturnType<typeof sprite_make>;
